import { PlusOutlined } from '@ant-design/icons';
import { Input, Tag, type TagProps, Tooltip } from 'antd';
import classnames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useFocus } from '@client/hooks/useFocus';
import { stub } from '@common/utils';

import styles from '../components.module.less';

import type { SizeType } from 'antd/es/config-provider/SizeContext';

type TTagsProps = {
  disabled: boolean;
  className?: TagProps['className'];
  forceUpdateForm?: boolean;
  canDelete: boolean;
  canUpdate: boolean;
  canCreate: boolean;
  onChange?: (tags: string[]) => void;
  size?: SizeType;
  newTag?: string;
  tags?: string[];
  truncateOn?: number;
  editWidth?: number;
};

export const EditableTags: React.FC<TTagsProps> = (props) => {
  const { t } = useTranslation();

  const {
    disabled,
    className,
    canDelete = false,
    canUpdate = false,
    canCreate = false,
    onChange = stub,
    size = 'small',
    newTag = t('actions.new'),
    tags = [],
    truncateOn = 20,
    editWidth = 150,
  } = props;

  const [updatedTags, setUpdatedTags] = useState<string[]>([]);
  const [inputVisible, setInputVisible] = useState(false);
  const [inputValue, setInputValue] = useState<string>('');
  const [editInputIndex, setEditInputIndex] = useState<number>(-1);
  const [editInputValue, setEditInputValue] = useState<string>('');

  // textInput must be declared here so the ref can refer to it
  const inputRef = useRef(null);
  const editRef = useRef(null);

  useEffect(() => {
    if (updatedTags.length !== tags.length) {
      setUpdatedTags(tags);
    }
  }, [tags, updatedTags.length]);

  const handleClose = (removedTag: string) => {
    const _updatedTags = updatedTags.filter((tag) => tag !== removedTag);
    setUpdatedTags(_updatedTags);
    onChange(_updatedTags);
  };

  useFocus({ condition: inputVisible, ref: inputRef });
  useFocus({ condition: editInputIndex > -1, ref: editRef });

  const showInput = () => {
    setInputVisible(!disabled && canCreate);
  };

  const handleInputChange = ({
    target: { value },
  }: {
    target: { value: string };
  }) => {
    if (canUpdate) setInputValue(value);
  };

  const handleInputConfirm = () => {
    if (inputValue && tags.indexOf(inputValue) === -1) {
      const _updatedTags = [...updatedTags, inputValue];

      const sorted = _updatedTags.sort();

      setUpdatedTags(sorted);
      onChange(sorted);
    }

    setInputVisible(false);
    setInputValue('');
  };

  const handleEditInputChange = ({
    target: { value },
  }: {
    target: { value: string };
  }) => {
    setEditInputValue(value);
  };

  const handleEditInputConfirm = () => {
    const newTags = [...updatedTags];
    newTags[editInputIndex] = editInputValue;

    const sorted = newTags.sort();

    setUpdatedTags(sorted);
    onChange(sorted);

    setEditInputValue('');
    setEditInputIndex(-1);
  };

  const inputProps = {
    size,
    disabled,
    type: 'text',
    style: { width: editWidth },
    className: styles.tagInput,
  };

  const isNewDisabled = disabled || !canCreate;

  return (
    <div className={classnames(styles.tags, className)}>
      {updatedTags?.map((tag: string, idx) => {
        if (editInputIndex === idx) {
          return (
            <Input
              {...inputProps}
              ref={editRef}
              key={tag + idx}
              value={editInputValue}
              onChange={handleEditInputChange}
              onBlur={handleEditInputConfirm}
              onPressEnter={handleEditInputConfirm}
            />
          );
        }

        const isLongTag = tag.length > truncateOn;

        const tagElem = (
          <Tag
            className={styles.editTag}
            key={tag}
            closable={!disabled && canDelete}
            onClose={() => handleClose(tag)}
          >
            <span
              onDoubleClick={(e) => {
                e.preventDefault();

                if (canUpdate) {
                  setEditInputIndex(idx);
                  setEditInputValue(tag);
                }
              }}
            >
              {isLongTag ? `${tag.slice(0, truncateOn)}...` : tag}
            </span>
          </Tag>
        );

        return isLongTag ? (
          <Tooltip title={tag} key={tag}>
            {tagElem}
          </Tooltip>
        ) : (
          tagElem
        );
      })}

      {inputVisible && (
        <Input
          {...inputProps}
          key={'create'}
          ref={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputConfirm}
          onPressEnter={handleInputConfirm}
        />
      )}

      {!inputVisible && (
        <Tag
          className={classnames({
            [styles.siteTagDisabled]: isNewDisabled,
            [styles.siteTagPlus]: !isNewDisabled,
          })}
          onClick={isNewDisabled ? stub : showInput}
        >
          <PlusOutlined /> {newTag}
        </Tag>
      )}
    </div>
  );
};
